import { Box, Chip, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { Result } from '../../types/results';
import type { PublicDisplayConfig } from '../../types/domain';
import type { TimeFormat } from '../../types/domain';
import { Podium, type PodiumEntry } from './Podium';
import { RankBadge } from './RankBadge';
import { AttemptsDetail } from './AttemptsDetail';
import { bestAttemptPerTeam } from './bestAttempt';

interface PodiumResultsViewProps {
  results: Result[];
  config: PublicDisplayConfig;
  highlightTeamId?: number | null;
  showAttempt?: boolean;
  timeFormat?: TimeFormat;
}

/** Serazeni: rank ASC (NP na konec, prazdne uplne na konec), stabilne dle startNumber. */
const sortByRank = (results: Result[]): Result[] =>
  [...results].sort((a, b) => {
    if (a.rank != null && b.rank != null) {
      const byRank = a.rank - b.rank;
      return byRank !== 0 ? byRank : a.teamStartNumber - b.teamStartNumber;
    }
    if (a.rank != null) return -1;
    if (b.rank != null) return 1;
    if (a.isNp && !b.isNp) return -1;
    if (!a.isNp && b.isNp) return 1;
    return a.teamStartNumber - b.teamStartNumber;
  });

export const PodiumResultsView = ({ results, config, highlightTeamId, showAttempt, timeFormat }: PodiumResultsViewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sorted = sortByRank(bestAttemptPerTeam(results));
  const topThree: PodiumEntry[] = sorted
    .filter((r) => r.rank != null && r.rank <= 3 && !r.isNp)
    .map((r) => ({
      teamId: r.teamId,
      rank: r.rank as 1 | 2 | 3,
      teamName: r.teamName,
      startNumber: r.teamStartNumber,
      primaryValue: r.timeFinalFormatted,
      primaryLabel: 'Čas',
      secondaryValue:
        config.showPenaltySeconds && r.penaltySeconds > 0 ? `+${r.penaltySeconds} s trest` : undefined,
    }));

  const rest = sorted.filter((r) => !topThree.some((t) => t.teamId === r.teamId));

  if (sorted.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Zatím žádné výsledky
        </Typography>
        <Typography variant="body2">Výsledky se objeví, jakmile je zapisovatel zadá.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {topThree.length > 0 && (
        <Podium entries={topThree} showStartNumber={config.showStartNumber} />
      )}

      {rest.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary', mb: 1, display: 'block' }}
          >
            Další pořadí
          </Typography>
          <Stack spacing={1}>
            {rest.map((r) => {
              const isHighlight = highlightTeamId === r.teamId;
              return (
                <Paper
                  key={r.id}
                  variant="outlined"
                  sx={{
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderColor: isHighlight ? 'primary.main' : 'divider',
                    bgcolor: isHighlight ? 'action.selected' : 'background.paper',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}
                >
                  <RankBadge rank={r.rank} size={32} />
                  {config.showStartNumber && (
                    <Chip size="small" label={`#${r.teamStartNumber}`} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={isHighlight ? 700 : 500} sx={{ wordBreak: 'break-word' }}>
                      {r.teamName}
                    </Typography>
                    {config.showNote && r.note && (
                      <Typography variant="caption" color="text.secondary">
                        {r.note}
                      </Typography>
                    )}
                    <AttemptsDetail
                      attempts={r.attempts}
                      showAttemptDetails={config.showAttemptDetails}
                      showLaneDetails={config.showLaneDetails}
                      compact
                      timeFormat={timeFormat}
                    />
                  </Box>
                  {showAttempt && (
                    <Typography variant="caption" color="text.secondary">
                      pokus {r.attemptNumber}
                    </Typography>
                  )}
                  {config.showPenaltySeconds && r.penaltySeconds > 0 && !r.isNp && (
                    <Chip
                      size="small"
                      label={`+${r.penaltySeconds} s`}
                      color="warning"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  )}
                  {r.isNp ? (
                    <Chip size="small" label="NP" color="error" variant="outlined" />
                  ) : r.timeFinalMs != null ? (
                    <Typography
                      fontFamily="monospace"
                      fontWeight={700}
                      sx={{ fontSize: '1.1rem', minWidth: 80, textAlign: 'right' }}
                    >
                      {r.timeFinalFormatted}
                    </Typography>
                  ) : (
                    <Typography
                      color="text.disabled"
                      sx={{ fontFamily: 'monospace', fontSize: '1.1rem', minWidth: 80, textAlign: 'right' }}
                    >
                      —
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );
};
